import axios from 'axios';

// REACT_APP_BACKEND_URL is an optional backend ORIGIN (no /api suffix).
// Unset/empty -> same-origin "/api" (served by Nginx in Docker).
// Present     -> "<origin>/api".
const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || '')
  .trim()
  .replace(/\/+$/, '');
const API = `${BACKEND_ORIGIN}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// User Registration API
export const registerUser = async (userData) => {
  const response = await apiClient.post('/users/register', userData);
  return response.data;
};

// Admin API
export const adminLogin = async (credentials) => {
  const response = await apiClient.post('/admin/login', credentials);
  return response.data;
};

export const getAllRegistrations = async () => {
  const response = await apiClient.get('/admin/registrations');
  return response.data;
};

export const getDashboardStats = async () => {
  const response = await apiClient.get('/admin/stats');
  return response.data;
};

export const exportRegistrations = async () => {
  const response = await apiClient.get('/admin/export', {
    responseType: 'blob',
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `registrations_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  
  return response.data;
};

// Get registrations with filters
export const getFilteredRegistrations = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.propertyType) params.append('propertyType', filters.propertyType);
  if (filters.municipality) params.append('municipality', filters.municipality);
  if (filters.search) params.append('search', filters.search);
  
  const response = await apiClient.get(`/admin/registrations?${params.toString()}`);
  return response.data;
};

// Get map markers for geolocated registrations
export const getMapMarkers = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.propertyType) params.append('propertyType', filters.propertyType);
  
  const response = await apiClient.get(`/admin/map-markers?${params.toString()}`);
  return response.data;
};

// Update registration status or other fields
export const updateRegistration = async (registrationId, updateData) => {
  const response = await apiClient.patch(`/admin/registrations/${registrationId}`, updateData);
  return response.data;
};

// ============================================================================
// ADMIN MANAGEMENT APIs
// ============================================================================

// Get all admin accounts
export const getAllAdmins = async () => {
  const response = await apiClient.get('/admin/admins');
  return response.data;
};

// Create new admin account
export const createAdmin = async (adminData) => {
  const response = await apiClient.post('/admin/admins', adminData);
  return response.data;
};

// Delete admin account
export const deleteAdmin = async (adminId) => {
  const response = await apiClient.delete(`/admin/admins/${adminId}`);
  return response.data;
};

// Change admin password
export const changeAdminPassword = async (adminId, newPassword) => {
  const response = await apiClient.put(`/admin/admins/${adminId}/password`, { newPassword });
  return response.data;
};

// ============================================================================
// DATA MAINTENANCE APIs
// ============================================================================

// Re-geocode registrations without coordinates
export const regeocodeRegistrations = async () => {
  const response = await apiClient.post('/admin/maintenance/regeocode');
  return response.data;
};

// Backfill nearest detachment for registrations
export const backfillDetachments = async () => {
  const response = await apiClient.post('/admin/maintenance/backfill-detachments');
  return response.data;
};

export default apiClient;
